import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LogOut, User } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { to: "/vehicles", label: "Vehicles" },
  { to: "/guides", label: "Guides" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-[#010F27]/95 backdrop-blur border-b border-[#29405F]" data-testid="site-navbar">
      <div className="container-bl flex items-center justify-between h-16">
        <Link to="/" onClick={() => setOpen(false)} className="flex flex-col leading-none" data-testid="nav-logo" aria-label="B&L Rentals home">
          <span className="font-heading font-extrabold text-white text-2xl tracking-tight">B&amp;L</span>
          <span className="font-heading font-semibold text-[#E2AB20] text-[10px] tracking-[0.32em] mt-0.5">RENTALS</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#E2AB20]" : "text-white hover:text-[#F4C542]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="nav-user-menu" className="rounded-lg">
                  <User size={16} className="mr-2" />
                  {user.name?.split(" ")[0] || "Account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="nav-dashboard">Dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/messages")} data-testid="nav-messages">Messages</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout">
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-white hover:text-[#F4C542]">Sign in</Link>
              <Link to="/vehicles" data-testid="nav-book-now" className="btn-primary text-sm py-2 px-4">Book Now</Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-md text-[#E2AB20]"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#29405F] bg-[#04162F]" data-testid="mobile-nav">
          <div className="container-bl py-3 flex flex-col gap-2">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2 text-white hover:text-[#F4C542]">
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <NavLink to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-white hover:text-[#F4C542]">Dashboard</NavLink>
                <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="py-2 text-left text-white">Sign out</button>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={() => setOpen(false)} className="py-2 text-white hover:text-[#F4C542]">Sign in</NavLink>
                <NavLink to="/vehicles" onClick={() => setOpen(false)} className="btn-primary text-sm py-2 px-4 w-fit">Book Now</NavLink>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
